/**
 * «Финансы → Счета»: наши счета — ИП и личные, — с оборотами и периодом, за
 * который есть операции. Счёт без операций заведён из перевода «самому
 * себе»: выписки по нему ещё нет, и P&L без неё неполный.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { NoAccess } from "@/components/NoAccess";
import { PNL, useCan } from "@/lib/access";
import { type Account, loadAccounts, money } from "@/lib/pnl";

const TH = "border-b border-border px-2.5 py-2 text-left";
const TD = "border-b border-border px-2.5 py-1.5";

function day(iso: string | null): string {
  return iso ? iso.split("-").reverse().join(".") : "—";
}

/** Номер счёта; у счёта Т-Банка без номера — продукт («ALL Airlines Premium»). */
function title(a: Account): string {
  return a.number ?? `«${a.product ?? "?"}» · номер неизвестен`;
}

export function AccountsPage() {
  return useCan(PNL) ? <AccountsScreen /> : <NoAccess what="счетам" />;
}

function AccountsScreen() {
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [failure, setFailure] = useState("");

  useEffect(() => {
    loadAccounts()
      .then(setAccounts)
      .catch((error: Error) => setFailure(error.message));
  }, []);

  const missing = (accounts ?? []).filter((a) => a.operations === 0).length;

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 [&>*]:flex-none">
      <div className="text-[12.5px] text-fg-muted">
        Счета заводятся сами — из выписок и из переводов самому себе. Счёт ИП (40802) и личный счёт того же человека
        (40817, 40820, 423) различаются по номеру: ИНН у них один.
        {missing > 0 ? (
          <span className="text-warn">
            {" "}
            Без выписки: {missing} — загрузите их в{" "}
            <Link to="/statements" className="underline">
              «Выписках»
            </Link>
            .
          </span>
        ) : null}
      </div>
      {failure ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          {failure}
        </div>
      ) : null}
      {accounts === null ? (
        <div className="text-[12.5px] text-fg-muted">…</div>
      ) : accounts.length === 0 ? (
        <div className="text-[12.5px] text-fg-muted">Счетов пока нет: они появятся после разбора первой выписки.</div>
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
                <th className={TH}>чей</th>
                <th className={TH}>счёт</th>
                <th className={TH}>вид</th>
                <th className={TH}>банк</th>
                <th className={TH}>период операций</th>
                <th className={`${TH} text-right`}>операций</th>
                <th className={`${TH} text-right`}>приход</th>
                <th className={`${TH} text-right`}>расход</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.id} data-annot={`счёт ${a.id}`} className="hover:bg-bg-hover">
                  <td className={TD}>{a.entrepreneur_name}</td>
                  <td className={`${TD} tabular-nums`}>
                    {a.operations > 0 ? (
                      <Link to={`/pnl/operations?account_id=${a.id}`} className="hover:underline">
                        {title(a)}
                      </Link>
                    ) : (
                      title(a)
                    )}
                  </td>
                  <td className={`${TD} ${a.kind === "personal" ? "text-accent-text" : "text-fg-muted"}`}>{a.kind_name}</td>
                  <td className={`${TD} text-fg-muted`}>{a.bank_name ?? "—"}</td>
                  <td className={`${TD} whitespace-nowrap tabular-nums text-fg-muted`}>
                    {a.operations > 0 ? `${day(a.date_from)} — ${day(a.date_to)}` : <span className="text-warn">нет выписки</span>}
                  </td>
                  <td className={`${TD} text-right tabular-nums`}>{a.operations}</td>
                  <td className={`${TD} text-right tabular-nums`}>{money(a.total_in)}</td>
                  <td className={`${TD} text-right tabular-nums`}>{money(a.total_out)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
